import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { ASSIGN_NUTRITION, UNASSIGN_NUTRITION, CLIENT_INTAKE } from 'api/graphql/nutrition';

const INTAKE_FIELDS = [
  ['weight', 'kg'], ['height', 'm'], ['age', ''], ['goal', ''], ['food_habits', ''],
  ['preferences', ''], ['cheat_meals', ''], ['dietary_restrictions', '']
];

const CLIENTS = gql`
  query AssignClients {
    users(role: CLIENT, first: 200, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
      data { id full_name }
    }
  }
`;
const PLANS = gql`
  query AssignPlans { plans(first: 100, page: 1) { data { id name } } }
`;

/**
 * Asigna un programa o recetario a un cliente (user_id) o a un plan (plan_id). Buscadores
 * (Autocomplete) en vez de dropdown, muestra a quién está asignado y permite QUITAR asignaciones.
 */
function AssignDialog({ open, programId = null, recipeBookId = null, assignments = [], onClose }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const [target, setTarget] = useState('client'); // 'client' | 'plan'
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState(assignments);
  const [dirty, setDirty] = useState(false);

  const { data: clientsData } = useQuery(CLIENTS, { skip: !open });
  const { data: plansData } = useQuery(PLANS, { skip: !open });
  const [assign, { loading }] = useMutation(ASSIGN_NUTRITION);
  const [unassign, { loading: removing }] = useMutation(UNASSIGN_NUTRITION);

  // #9b: ver los datos del cliente seleccionado sin salir del modal de asignar.
  const { data: intakeData, loading: intakeLoading } = useQuery(CLIENT_INTAKE, {
    variables: { user_id: selected?.id },
    skip: !(target === 'client' && selected),
    fetchPolicy: 'cache-and-network'
  });
  const intake = intakeData?.clientIntake;

  const clients = (clientsData?.users?.data ?? []).map((c) => ({ id: c.id, label: c.full_name }));
  const plans = (plansData?.plans?.data ?? []).map((p) => ({ id: p.id, label: p.name }));
  const options = target === 'client' ? clients : plans;

  const handleAssign = async () => {
    const isGeneral = target === 'general';
    if (!isGeneral && !selected) return;
    await assign({
      variables: {
        nutrition_program_id: programId ?? null,
        recipe_book_id: recipeBookId ?? null,
        user_id: target === 'client' ? selected.id : null,
        plan_id: target === 'plan' ? selected.id : null,
        general: isGeneral
      }
    });
    // Refleja la nueva asignación en los chips sin cerrar.
    setItems((prev) => [...prev, { id: `tmp-${isGeneral ? 'all' : selected.id}`, label: isGeneral ? t('toEveryone', { ns: 'nutrition', defaultValue: 'Todos' }) : selected.label }]);
    setSelected(null);
    setDirty(true);
  };

  const handleUnassign = async (id) => {
    if (String(id).startsWith('tmp-')) { onClose(true); return; }
    await unassign({ variables: { id } });
    setItems((prev) => prev.filter((a) => a.id !== id));
    setDirty(true);
  };

  return (
    <Dialog open={open} onClose={() => onClose(dirty)} fullWidth maxWidth="sm" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{t('assignTitle', { ns: 'nutrition', defaultValue: 'Asignar' })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Ya asignado a (chips removibles) */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>
              {t('alreadyAssigned', { ns: 'nutrition', defaultValue: 'Ya asignado a' })}
            </Typography>
            {items.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {items.map((a) => (
                  <Chip
                    key={a.id}
                    size="small"
                    label={a.label}
                    onDelete={removing ? undefined : () => handleUnassign(a.id)}
                    sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'gold.main' } }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>{t('notAssignedYet', { ns: 'nutrition', defaultValue: 'Aún no está asignado.' })}</Typography>
            )}
          </Box>

          <Divider />

          <ToggleButtonGroup exclusive size="small" value={target} onChange={(_, v) => { if (v) { setTarget(v); setSelected(null); } }}>
            <ToggleButton value="client" sx={{ textTransform: 'none' }}>{t('toClient', { ns: 'nutrition', defaultValue: 'A un cliente' })}</ToggleButton>
            <ToggleButton value="plan" sx={{ textTransform: 'none' }}>{t('toPlan', { ns: 'nutrition', defaultValue: 'A un plan' })}</ToggleButton>
            <ToggleButton value="general" sx={{ textTransform: 'none' }}>{t('toAll', { ns: 'nutrition', defaultValue: 'A todos' })}</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {target === 'general' ? (
              <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                {t('toAllHint', { ns: 'nutrition', defaultValue: 'Se asignará a TODOS los clientes (general).' })}
              </Typography>
            ) : (
              <Autocomplete
                value={selected}
                onChange={(_, v) => setSelected(v)}
                options={options}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                getOptionLabel={(o) => o.label ?? ''}
                noOptionsText={t('noResults', { ns: 'nutrition', defaultValue: 'Sin resultados' })}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={target === 'client' ? t('client', { ns: 'nutrition', defaultValue: 'Cliente' }) : t('plan', { ns: 'nutrition', defaultValue: 'Plan' })} placeholder={t('searchPlaceholder', { ns: 'nutrition', defaultValue: 'Buscar...' })} />
                )}
              />
            )}
            <Button variant="contained" color="primary" onClick={handleAssign} disabled={loading || (target !== 'general' && !selected)} sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}>{t('assign', { ns: 'nutrition', defaultValue: 'Asignar' })}</Button>
          </Stack>

          {/* Datos del cliente seleccionado (intake) para decidir qué asignar, sin cambiar de pestaña. */}
          {target === 'client' && selected && (
            <Box sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised', p: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>{t('intakeViewerSubtitle', { ns: 'nutrition', defaultValue: 'Datos de admisión del cliente' })}</Typography>
              {intakeLoading && !intake && <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={18} sx={{ color: 'secondary.main' }} /></Box>}
              {!intakeLoading && !intake && <Typography variant="body2" sx={{ color: 'text.disabled' }}>{t('noIntake', { ns: 'nutrition', defaultValue: 'Este cliente aún no ha llenado sus datos.' })}</Typography>}
              {intake && (
                <Grid container spacing={1}>
                  {INTAKE_FIELDS.map(([field, unit]) => (
                    <Grid xs={6} sm={3} key={field}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 700, display: 'block', fontSize: 10 }}>{t(field, { ns: 'nutrition' })}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{intake[field] ? `${intake[field]}${unit ? ` ${unit}` : ''}` : '—'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={() => onClose(dirty)} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>{t('close', { ns: 'buttons', defaultValue: 'Cerrar' })}</Button>
      </DialogActions>
    </Dialog>
  );
}

AssignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  recipeBookId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignments: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired
};

export default AssignDialog;
